import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { flagIntro } from "@/lib/introFlag";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, Logo } from "@/components/AuthShell";
import { InstallApp } from "@/components/InstallApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [remember, setRemember] = useState(false);

  const homeFor = (u) =>
    u.profilo_accesso === "AMMINISTRATORE" ? "/admin" : u.profilo_accesso === "UFFICIO" ? "/ufficio" : "/lavoratore";

  useEffect(() => {
    api.get("/auth/demo-config")
      .then(({ data }) => setDemoAccounts(data?.enabled ? (data.accounts || []) : []))
      .catch(() => setDemoAccounts([]));
  }, []);

  const goHome = (user) => {
    const home = user.profilo_accesso === "AMMINISTRATORE" ? "/admin" : user.profilo_accesso === "UFFICIO" ? "/ufficio" : "/lavoratore";
    navigate(home);
  };

  const demoLogin = async (key) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/demo-login", { demo_key: key });
      await login(data.token, data.user, remember);
      goHome(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await login(data.token, data.user, remember);
      flagIntro();
      const home = data.user.profilo_accesso === "AMMINISTRATORE" ? "/admin" : data.user.profilo_accesso === "UFFICIO" ? "/ufficio" : "/lavoratore";
      navigate(home);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {authLoading ? (
        <div className="flex items-center justify-center py-16" data-testid="auth-bootstrap-loader">
          <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : user ? (
        <Navigate to={homeFor(user)} replace />
      ) : (
      <>
      <div className="flex justify-center mb-8">
        <Logo size="lg" />
      </div>
      <Card className="rounded-2xl border-slate-200 shadow-lg shadow-slate-200/50">
        <CardContent className="p-6 sm:p-8">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Accedi</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Registro Digitale delle Prestazioni Lavorative</p>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="login-error"
              className="mb-4 rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email} data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)} placeholder="nome@orbit.it" autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={show ? "text" : "password"} required value={password}
                  data-testid="login-password-input" className="pr-10"
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password"
                />
                <button
                  type="button" onClick={() => setShow(!show)} data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox" checked={remember} data-testid="remember-me-checkbox"
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-2 focus:ring-primary/30 cursor-pointer"
                />
                Rimani connesso
              </label>
              <Link to="/password-dimenticata" data-testid="forgot-password-link"
                className="text-sm text-primary font-medium hover:underline">
                Password dimenticata?
              </Link>
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full rounded-lg h-11 font-semibold transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>

          <InstallApp />
        </CardContent>
      </Card>

      {demoAccounts.length > 0 && (
        <div className="mt-4 text-center" data-testid="demo-access-section">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Accesso demo</p>
          <div className="flex flex-wrap justify-center gap-2">
            {demoAccounts.map((a) => (
              <button key={a.key} type="button" disabled={loading} onClick={() => demoLogin(a.key)}
                data-testid={`demo-login-${a.key}`}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-all disabled:opacity-50">
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </AuthShell>
  );
}
